import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import { IEditProfile, IUserData } from "../../../models/common/IUserProfile";
import { useEffect, useState } from "react";
import { baseURL, cloneDeep } from "../../../constants/commonConstants";
import { alertConstants, getAlertData } from "../../../helper/sweet-alert";
import FloatingLabelTextInput from "../../floatingtextinput";
import {
  getCurrentUserData,
  updateUserProfile,
} from "../../../action/user/userProfileActionCreator";
import LoaderSpinner from "../../loader";
import { UserRole } from "../../../constants/RoleEnum";
import CropComponent from "../../cropComponent";
import ReactModal from "react-modal";

const ProfileEdit = (props: IEditProfile) => {
  const { updateUserProfile, user, setShowEdit, getUser } = props;
  const dispatch = useDispatch();
  // states

  const [data, setData] = useState<IUserData>(cloneDeep(user));
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | undefined>(undefined);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };

    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      const fileType = selectedFile.type;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else {
        setData({ ...data, file: selectedFile });
        setShowCropModal(true);
      }
    }
  };

  useEffect(() => {
    if (user) {
      let _user = cloneDeep(user);
      if (_user.image) {
        const imageUrl = baseURL + _user.image;
        setImagePreview(imageUrl);
      }
    }
  }, [user]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = async () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      setShowEdit(false);
      getUser && (await getUser());
    }
  };

  const onUpdate = async () => {
    const formData = new FormData();
    formData.append("displayName", data.displayName ?? " ");
    formData.append("file", data.file);
    setIsLoading(true);
    try {
      await updateUserProfile(formData, setAlert);
    } finally {
      getUser && getUser();
    }
    setIsLoading(false);
  };

  // image cropping
  const handleCropComplete = (croppedFile: File) => {
    setCroppedImage(croppedFile);
    setData({ ...data, file: croppedFile });
    const fileURL = URL.createObjectURL(croppedFile);
    setImagePreview(fileURL);
    setFileName(croppedFile.name);
    setShowCropModal(false);
  };

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h2 className="modal-title fs-5" id="editprofileLabel">
              Edit Profile
            </h2>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="avatar-upload">
              <div className="avatar-edit">
                <input
                  type="file"
                  name="file"
                  id="imageUpload"
                  accept=".png, .jpg, .jpeg"
                  onChange={onFileChange}
                  style={{ display: "none" }}
                />
                <label htmlFor="imageUpload" className="upload-label"></label>
              </div>
              <div className="avatar-preview">
                <img
                  className="profile-pic"
                  src={imagePreview || ""}
                  onError={(e) => {
                    e.currentTarget.src =
                      "/assets/images/icon/DefaultImage.jpeg";
                  }}
                ></img>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 form-group">
                <FloatingLabelTextInput
                  label={"Username"}
                  onChange={onChange}
                  isMandatory={false}
                  value={data?.displayName}
                  name={"displayName"}
                />
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"First Name"}
                  onChange={onChange}
                  isMandatory={false}
                  value={data?.firstName}
                  name={"firstName"}
                  disable
                />
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Last Name"}
                  onChange={onChange}
                  isMandatory={false}
                  value={data?.lastName}
                  name={"lastName"}
                  disable
                />
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Phone"}
                  onChange={onChange}
                  isMandatory={false}
                  value={data.phone}
                  name={"phone"}
                  disable={true}
                />
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Email"}
                  onChange={onChange}
                  isMandatory={false}
                  value={data.email}
                  // value={data?.email === "demo-staff@analystortech.com"
                  //   ? "robert1@andrews.edu"
                  //   : data?.email === "demo-faculty@analystortech.com"
                  //       ? "drobney2@andrews.edu"
                  //       : data?.email}
                  name={"email"}
                  disable={true}
                />
              </div>
              {user?.currentRole == UserRole.Student && (
                <>
                  <div className="col-md-6 form-group">
                    <FloatingLabelTextInput
                      label={"Current Term"}
                      onChange={onChange}
                      isMandatory={false}
                      value={data.currentTerm}
                      name={"currentTerm"}
                      disable={true}
                    />
                  </div>
                  <div className="col-md-6 form-group">
                    <FloatingLabelTextInput
                      label={"Program of Study"}
                      onChange={onChange}
                      isMandatory={false}
                      value={data.currentCourse}
                      name={"currentCourse"}
                      disable={true}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
              >
                Save changes
              </button>
            </div>
          </div>

          <ReactModal
            isOpen={showCropModal}
            onRequestClose={() => setShowCropModal(false)}
            contentLabel="Crop Image"
            ariaHideApp={false}
            shouldCloseOnOverlayClick={false}
            style={{
              overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 1050,
              },
              content: {
                width: "fit-content",
                margin: "auto",
                borderRadius: "10px",
                zIndex: 1100,
                position: "relative",
              },
            }}
          >
            {data?.id && (
              <CropComponent
                onCropComplete={handleCropComplete}
                onClose={() => setShowCropModal(false)}
                initialImage={data.file}
              />
            )}
          </ReactModal>

          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {isLoading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateUserProfile: (data: any, callBack: Function) =>
    updateUserProfile(data, callBack),
  getUser: () => getCurrentUserData(),
};

function mapStateToProps(state: any) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(ProfileEdit);
